import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property, PropertyString } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { Button } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Button";
import { Card, CardPropertyValues, ICard } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Card";
import { NAMESPACE } from "./_Namespace";

export type UserNotificationPropertyValues =
    CardPropertyValues
    & {
        targetUrl: string;
    };

export type UserNotificationProperties = {
    [name in keyof UserNotificationPropertyValues]: Property<UserNotificationPropertyValues[name]>
};

export interface IUserNotification extends ICard, UserNotificationProperties
{
}

export class UserNotification extends Card implements IUserNotification
{
    public static override classNamespace = NAMESPACE + "UserNotification";
    public static override className = "UserNotification";

    // #region Global Variables

    protected buttonDismiss?: Button;
    protected buttonOpen?: Button;

    // #endregion

    // #region Properties

    public targetUrl!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<UserNotificationPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _targetUrlChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this.buttonDismiss, "this.buttonDismiss");
        Assert.isNotNullOrUndefined(this.buttonOpen, "this.buttonOpen");

        if (!StringHelper.isNullOrUndefinedOrWhitespace(change.currentValue))
        {
            this.buttons.set([
                this.buttonOpen,
                this.buttonDismiss
            ]);
        }
        else
        {
            this.buttons.set([
                this.buttonDismiss
            ]);
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: UserNotificationPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.targetUrl = new PropertyString(this, propertyValues.targetUrl, this._targetUrlChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.buttonDismiss, "this.buttonDismiss");
        Assert.isNotNullOrUndefined(this.buttonOpen, "this.buttonOpen");

        this.buttonDismiss.on("click", { self: this }, this.onButtonDismissClicked);
        this.buttonOpen.on("click", { self: this }, this.onButtonOpenClicked);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.buttonDismiss =
            new Button({ cssClass: "rad-action-cancel", tag: "dismiss", text: "Dismiss" });

        this.buttonOpen =
            new Button({ text: "Open", tag: "open" });

        return $$element;
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-user-notification");

        // Trigger Property Changes
        this.targetUrl.triggerChange();
    }

    // #endregion

    // #region Event Handlers

    protected onButtonDismissClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self.trigger("userNotificationButtonClicked", { button: self.buttonDismiss, notification: self });
    }
    protected onButtonOpenClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self.trigger("userNotificationButtonClicked", { button: self.buttonOpen, notification: self });
    }

    // #endregion
}

